/**
 * Cloud access for `transactions` <- localStorage `mycryptoplanner_transactions`.
 *
 * The `type` column has a CHECK (type in ('BUY','SELL','TRANSFER')). The app's
 * `Transaction.type` is the literal union `'BUY' | 'SELL' | 'TRANSFER'`, so the
 * stored values already match the allowed set exactly — no transform needed.
 */
import type { Transaction } from '../../types';
import type { TransactionRow } from './rows';
import { makeAppendTable } from './client';

function toRow(t: Transaction, userId: string): TransactionRow {
  return {
    id: t.id,
    user_id: userId,
    coin_id: t.coinId,
    symbol: t.symbol,
    name: t.name,
    type: t.type, // 'BUY' | 'SELL' | 'TRANSFER' — matches CHECK exactly
    amount: t.amount,
    price: t.price,
    date: t.date,
    timestamp_ms: t.timestamp,
    wallet: t.wallet ?? null,
    from_wallet: t.fromWallet ?? null,
    to_wallet: t.toWallet ?? null,
    network_fee: t.networkFee ?? null,
    notes: t.notes ?? null,
    entry_amount: t.entryAmount ?? null,
    entry_currency: t.entryCurrency ?? null,
    fx_rate_usd: t.fxRateUsd ?? null,
    fx_rate_aud: t.fxRateAud ?? null,
    fx_rate_date: t.fxRateDate ?? null,
    fx_source: t.fxSource ?? null,
    fee: t.fee ?? null,
    fee_currency: t.feeCurrency ?? null,
    fee_usd: t.feeUsd ?? null,
    fee_aud: t.feeAud ?? null,
  };
}

function fromRow(row: TransactionRow): Transaction {
  return {
    id: row.id,
    coinId: row.coin_id,
    symbol: row.symbol,
    name: row.name,
    type: row.type,
    amount: Number(row.amount),
    price: Number(row.price),
    date: row.date,
    timestamp: Number(row.timestamp_ms),
    ...(row.wallet != null ? { wallet: row.wallet } : {}),
    ...(row.from_wallet != null ? { fromWallet: row.from_wallet } : {}),
    ...(row.to_wallet != null ? { toWallet: row.to_wallet } : {}),
    ...(row.network_fee != null ? { networkFee: Number(row.network_fee) } : {}),
    ...(row.notes != null ? { notes: row.notes } : {}),
    // FX capture. Absent columns (a client reading rows written before the
    // migration, or written by an older client) stay absent on the app object —
    // never defaulted, because a fabricated rate is worse than a missing one.
    ...(row.entry_amount != null ? { entryAmount: Number(row.entry_amount) } : {}),
    ...(row.entry_currency != null ? { entryCurrency: row.entry_currency } : {}),
    ...(row.fx_rate_usd != null ? { fxRateUsd: Number(row.fx_rate_usd) } : {}),
    ...(row.fx_rate_aud != null ? { fxRateAud: Number(row.fx_rate_aud) } : {}),
    ...(row.fx_rate_date != null ? { fxRateDate: row.fx_rate_date } : {}),
    ...(row.fx_source != null ? { fxSource: row.fx_source as Transaction['fxSource'] } : {}),
    // Trading fee. Same rule as above: an absent column stays absent rather than
    // becoming a zero, so a pre-fee row is indistinguishable from a no-fee row —
    // which is exactly what it is.
    ...(row.fee != null ? { fee: Number(row.fee) } : {}),
    ...(row.fee_currency != null ? { feeCurrency: row.fee_currency } : {}),
    ...(row.fee_usd != null ? { feeUsd: Number(row.fee_usd) } : {}),
    ...(row.fee_aud != null ? { feeAud: Number(row.fee_aud) } : {}),
  };
}

export const transactionsTable = makeAppendTable<Transaction, TransactionRow>(
  'transactions',
  toRow,
  fromRow
);
